/**
 * components/branch/BranchAvailabilityGrid.js
 * ─────────────────────────────────────────────────────────────────────────────
 * The "Branches" tab of a catalogue (4 Oct 2026): what each product is like at
 * one of the vendor's locations. Izi's decision: a branch manages its own
 * availability, sold out and prices; head office manages the product itself.
 *
 *   On sale here   switch it off at this branch only
 *   Sold out       "For today" (clears itself next business day) or
 *                  "Until I switch it back"
 *   Price here     replaces the head office price at this branch only;
 *                  "Use head office price" clears it. Shown only while the
 *                  server's branch price switch is on.
 *
 * No row on the server means: on sale, not sold out, head office price.
 * Every write goes to PATCH /api/catalog/branches/:owner/products[/:id] and
 * the server checks the exact key at the exact location, so a branch member
 * can change only their own branches. The controls follow the same rule
 * through useVendorAccess().canAt, and refusals show the server's message.
 *
 * Shared by the brand, restaurant and pharmacy catalogues; each passes its
 * products through productsForGrid.
 *
 * `branchMenus` (restaurants): { [outletId]: productIds | null }, the dishes a
 * branch with its own menu sells. A dish left off that menu is not sold there
 * whatever its switch says, so its row says so. A branch with no menu of its
 * own (null) sells the whole head office menu.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { AppButton, AppSelect } from "../ui";
import SearchBox from "../SearchBox";
import { apiSetBranchProduct, apiSetBranchProducts } from "../../api/branchCatalog.js";
import { useBranchScope, useVendorLocations } from "../../context/BranchScopeContext.js";
import { useVendorAccess } from "../../hooks/useVendorAccess.js";
import useBranchStates from "../../hooks/useBranchStates.js";
import { useCountry } from "../../context/CountryContext.js";
import { formatCurrency } from "../../config/markets.js";
import { branchAwareMessage, locationKeyOf } from "../../utilities/vendorScope.js";
import { locationRefusal } from "../team/Gated.js";
import "./branch.css";

const SOLD_OUT_OPTIONS = [
  { value: "", label: "Not sold out" },
  { value: "TODAY", label: "For today" },
  { value: "UNTIL_BACK", label: "Until I switch it back" },
];

/**
 * One product row for the grid, from any vertical's product shape. Products
 * that appear in two sections are listed once.
 */
export const productsForGrid = (products = []) => {
  const seen = new Set();
  const out = [];
  for (const p of products || []) {
    const id = p?._id || p?.id;
    if (!id || seen.has(String(id))) continue;
    seen.add(String(id));
    const image = p.image || (Array.isArray(p.images) ? p.images[0]?.url || p.images[0] : null) || null;
    out.push({
      id: String(id),
      name: p.name || p.title || "Unnamed product",
      image: typeof image === "string" ? image : null,
      price: Number.isFinite(Number(p.price)) ? Number(p.price) : null,
      priceOnRequest: p.priceOnRequest === true || p.pricingMode === "PRICE_ON_REQUEST",
      enabled: p.enabled !== false && p.isActive !== false,
    });
  }
  return out;
};

/** The sold out select's value for a row: the live mode, or "" when on sale. */
export const soldOutValueOf = (row) => (row?.soldOut?.live ? row.soldOut.until || "TODAY" : "");

/** What a PATCH body for a sold out choice looks like. */
export const soldOutPatchOf = (value) =>
  value ? { soldOut: { active: true, until: value } } : { soldOut: { active: false } };

/**
 * The line above the table that says what a change here reaches. Head office
 * is also told where "everywhere" lives, so one branch and every branch are
 * never confused (4 Oct 2026).
 */
export const gridScopeLine = (where, { isBranchScoped = false } = {}) =>
  isBranchScoped
    ? `Changes on this tab apply to ${where} only.`
    : `Changes on this tab apply to ${where} only. To stop selling a product at every branch, switch it off on the Products tab.`;

/** Why the controls are locked, as one plain note; null when they are not. */
export const lookOnlyNote = (refusal) => {
  if (!refusal) return null;
  const reason = /[.!?]$/.test(refusal) ? refusal : `${refusal}.`;
  return `You can look at this branch, but you cannot change it. ${reason}`;
};

/** The location the grid opens on: the navbar's branch when it is one, else the first listed. */
export const initialGridLocation = (active, locations) => {
  const key = locationKeyOf(active);
  if (key && locations.some((l) => l.id === key)) return key;
  return locations[0]?.id || null;
};

/** The dishes a branch's own menu lists, or null when it sells everything. */
export const branchMenuSet = (branchMenus, location) => {
  const ids = branchMenus && location ? branchMenus[locationKeyOf(location)] : null;
  return Array.isArray(ids) && ids.length ? new Set(ids.map((id) => String(id?._id ?? id).toLowerCase())) : null;
};

export const NOT_ON_BRANCH_MENU = "Not on this branch's menu, so not on sale here. Head office sets the menu under Outlets.";

export default function BranchAvailabilityGrid({ vendorUserId, products = [], onChanged, branchMenus = null }) {
  const { activeCountry } = useCountry();
  const money = useCallback((n) => formatCurrency(n, activeCountry), [activeCountry]);
  const access = useVendorAccess();
  const { active } = useBranchScope();
  const { loaded, locations, branchPricesEnabled } = useVendorLocations(vendorUserId);
  const rowsIn = useMemo(() => productsForGrid(products), [products]);

  const [location, setLocation] = useState(null);
  const { states, loading, error, merge } = useBranchStates(vendorUserId, location);
  const [savingId, setSavingId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [drafts, setDrafts] = useState({});
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!loaded) return;
    setLocation((current) => (current && locations.some((l) => l.id === current) ? current : initialGridLocation(active, locations)));
  }, [loaded, locations, active]);

  // Each control says why it is locked: the role, the branch, or head office
  // (components/team/Gated.js locationRefusal).
  const switchRefusal = location ? locationRefusal(access, "catalogue:activate", location) : undefined;
  const priceRefusal = location ? locationRefusal(access, "pricing:edit", location) : undefined;
  const canSwitch = Boolean(location) && !switchRefusal;
  const canPrice = Boolean(location) && !priceRefusal;
  const here = locations.find((l) => l.id === location) || null;
  const menuHere = useMemo(() => branchMenuSet(branchMenus, location), [branchMenus, location]);

  // A new branch starts with nothing selected and no half-typed prices.
  useEffect(() => {
    setSelected(new Set());
    setDrafts({});
  }, [location]);

  const saveOne = async (product, patch, successText) => {
    setSavingId(product.id);
    try {
      const { data } = await apiSetBranchProduct(vendorUserId, product.id, { location, ...patch });
      merge(data?.rows);
      if (successText) toast.success(successText);
      if (typeof onChanged === "function") onChanged();
      return true;
    } catch (err) {
      toast.error(branchAwareMessage(err, "Could not save this change."));
      return false;
    } finally {
      setSavingId(null);
    }
  };

  const saveBulk = async (patch, successText) => {
    const productIds = [...selected];
    if (!productIds.length) return;
    setBulkBusy(true);
    try {
      const { data } = await apiSetBranchProducts(vendorUserId, { location, productIds, ...patch });
      merge(data?.rows);
      toast.success(successText(productIds.length));
      setSelected(new Set());
      if (typeof onChanged === "function") onChanged();
    } catch (err) {
      toast.error(branchAwareMessage(err, "Could not save these changes."));
    } finally {
      setBulkBusy(false);
    }
  };

  const commitPrice = (product) => {
    const draft = drafts[product.id];
    if (draft === undefined) return;
    const stored = states.get(product.id)?.price ?? null;
    const text = String(draft).trim();
    if (!text) {
      setDrafts((d) => ({ ...d, [product.id]: undefined }));
      return;
    }
    const value = Number(text.replace(/,/g, ""));
    if (!Number.isFinite(value) || value <= 0) {
      toast.error("Enter a price above zero.");
      return;
    }
    if (value === stored) {
      setDrafts((d) => ({ ...d, [product.id]: undefined }));
      return;
    }
    saveOne(product, { price: value }, `${product.name} now costs ${money(value)} at ${here?.name || "this branch"}.`).then(
      (ok) => ok && setDrafts((d) => ({ ...d, [product.id]: undefined }))
    );
  };

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? rowsIn.filter((p) => p.name.toLowerCase().includes(q)) : rowsIn;
  }, [rowsIn, query]);

  if (!loaded) return <div className="lk-loading lk-loading--panel">Loading branches...</div>;
  if (!locations.length) {
    return <div className="lk-note lk-note--info">This business has no branches yet.</div>;
  }

  const allSelected = visible.length > 0 && visible.every((p) => selected.has(p.id));
  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(visible.map((p) => p.id)));
  const toggleOne = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const where = here?.name || "this branch";
  const lockedTitle = switchRefusal;
  const lockedNote = lookOnlyNote(switchRefusal);

  return (
    <div className="lk-branch-grid">
      <div className="lk-branch-grid__bar">
        <div className="lk-field">
          <label className="lk-field__label" htmlFor="branch-grid-location">Branch</label>
          <AppSelect
            id="branch-grid-location"
            block
            value={location || ""}
            onChange={(e) => setLocation(e.target.value)}
          >
            {locations.map((l) => (
              <option key={l.id} value={l.id}>
                {l.isActive === false ? `${l.name} (closed)` : l.name}
              </option>
            ))}
          </AppSelect>
        </div>
        <SearchBox value={query} onChange={setQuery} placeholder="Search products" />
        {canSwitch && selected.size > 0 && (
          <div className="lk-branch-grid__bulk" aria-label="Change the selected products">
            <span className="lk-field__hint" style={{ marginTop: 0 }}>{selected.size} selected</span>
            <AppButton size="sm" variant="outline" loading={bulkBusy}
              onClick={() => saveBulk({ available: true }, (n) => `${n} on sale at ${where}.`)}>
              On sale here
            </AppButton>
            <AppButton size="sm" variant="quiet" disabled={bulkBusy}
              onClick={() => saveBulk({ available: false }, (n) => `${n} off at ${where}.`)}>
              Not on sale here
            </AppButton>
            <AppButton size="sm" variant="quiet" disabled={bulkBusy}
              onClick={() => saveBulk(soldOutPatchOf("TODAY"), (n) => `${n} sold out today at ${where}.`)}>
              Sold out today
            </AppButton>
            <AppButton size="sm" variant="quiet" disabled={bulkBusy}
              onClick={() => saveBulk(soldOutPatchOf(""), (n) => `${n} back on sale at ${where}.`)}>
              Back on sale
            </AppButton>
          </div>
        )}
      </div>

      <p className="lk-field__hint" style={{ marginTop: 0, marginBottom: 10 }}>
        {gridScopeLine(where, { isBranchScoped: Boolean(access.isBranchScoped) })}
      </p>
      {lockedNote ? <div className="lk-note lk-note--warning" style={{ marginBottom: 12 }}>{lockedNote}</div> : null}
      {error ? <div className="lk-note lk-note--danger" style={{ marginBottom: 12 }}>{error}</div> : null}

      <div className="lk-table-frame">
        <div className="table-responsive">
          <table className="table lk-table">
            <thead>
              <tr>
                <th style={{ width: 36 }}>
                  <input
                    type="checkbox"
                    aria-label="Select every product shown"
                    checked={allSelected}
                    disabled={!canSwitch || !visible.length}
                    onChange={toggleAll}
                  />
                </th>
                <th>Product</th>
                <th>On sale here</th>
                <th>Sold out</th>
                {branchPricesEnabled && <th>Price here</th>}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={branchPricesEnabled ? 5 : 4} className="text-center">Loading...</td></tr>
              )}
              {!loading && !visible.length && (
                <tr><td colSpan={branchPricesEnabled ? 5 : 4} className="text-center">No products match.</td></tr>
              )}
              {!loading && visible.map((p) => {
                const row = states.get(p.id) || null;
                const available = row ? row.available !== false : true;
                const soldOut = soldOutValueOf(row);
                const branchPrice = row?.price ?? null;
                const differs = branchPrice !== null && branchPrice !== p.price;
                const busy = savingId === p.id || bulkBusy;
                const draft = drafts[p.id];
                const offMenu = Boolean(menuHere) && !menuHere.has(p.id.toLowerCase());
                return (
                  <tr key={p.id} className={!available || soldOut || offMenu ? "is-off" : undefined}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Select ${p.name}`}
                        checked={selected.has(p.id)}
                        disabled={!canSwitch}
                        onChange={() => toggleOne(p.id)}
                      />
                    </td>
                    <td>
                      <div className="lk-branch-grid__product">
                        {p.image ? <img className="lk-branch-grid__thumb" src={p.image} alt="" /> : <span className="lk-branch-grid__thumb" />}
                        <span style={{ minWidth: 0 }}>
                          <span className="lk-branch-grid__name">{p.name}</span>
                          {!p.enabled && (
                            <span className="lk-table-meta">Switched off by head office, so not on sale anywhere</span>
                          )}
                          {p.enabled && offMenu && <span className="lk-table-meta">{NOT_ON_BRANCH_MENU}</span>}
                        </span>
                      </div>
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        role="switch"
                        className="lk-branch-grid__switch"
                        aria-label={`${p.name} on sale at ${where}`}
                        checked={available}
                        disabled={!canSwitch || busy}
                        title={lockedTitle}
                        onChange={() =>
                          saveOne(p, { available: !available }, `${p.name} ${available ? "is off" : "is on sale"} at ${where}.`)
                        }
                      />
                    </td>
                    <td style={{ minWidth: 190 }}>
                      <AppSelect
                        size="sm"
                        value={soldOut}
                        disabled={!canSwitch || busy}
                        aria-label={`${p.name} sold out at ${where}`}
                        onChange={(e) =>
                          saveOne(
                            p,
                            soldOutPatchOf(e.target.value),
                            e.target.value ? `${p.name} is sold out at ${where}.` : `${p.name} is back on sale at ${where}.`
                          )
                        }
                        options={SOLD_OUT_OPTIONS}
                      />
                    </td>
                    {branchPricesEnabled && (
                      <td>
                        {p.priceOnRequest ? (
                          <span className="lk-table-meta" style={{ marginTop: 0 }}>Price on request</span>
                        ) : (
                          <div className="lk-branch-grid__price">
                            <input
                              className="lk-input"
                              inputMode="decimal"
                              aria-label={`${p.name} price at ${where}`}
                              placeholder={p.price !== null ? `Head office price: ${money(p.price)}` : "Head office price"}
                              value={draft !== undefined ? draft : branchPrice !== null ? String(branchPrice) : ""}
                              disabled={!canPrice || busy}
                              title={priceRefusal}
                              onChange={(e) => {
                                // Read now: the updater runs later, after React has put the
                                // controlled value back.
                                const typed = e.target.value;
                                setDrafts((d) => ({ ...d, [p.id]: typed }));
                              }}
                              onBlur={() => commitPrice(p)}
                              onKeyDown={(e) => e.key === "Enter" && commitPrice(p)}
                            />
                            {differs && <span className="lk-pill lk-branch-chip lk-branch-chip--warn">Differs from head office</span>}
                            {branchPrice !== null && (
                              <AppButton
                                size="sm"
                                variant="quiet"
                                disabled={!canPrice || busy}
                                onClick={() => saveOne(p, { price: null }, `${p.name} uses the head office price at ${where}.`)}
                              >
                                Use head office price
                              </AppButton>
                            )}
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <p className="lk-field__hint">
        Sold out for today clears itself at the start of the next business day. A product head office switches off is not on sale at any branch.
      </p>
    </div>
  );
}
